import { useState, forwardRef, useImperativeHandle } from 'react';
import i18n from '../../i18n/i18n';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';

// Components
import TitleContentInfo from '../TitleContentInfo';
import SignEvalTextField from '../SignEvalTextField';
import SignEvalButton from '../SignEvalButton';
import FloatingButton from '../Button/FloatingButton';

// MUI imports
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { Autocomplete } from '@mui/material';
import { Select, MenuItem, InputLabel, FormControl } from '@mui/material';
import Button from '@mui/material/Button';

// Helper
import { useTheme } from '../../hooks/useTheme';
import useTrainee from '../../hooks/useTrainee';
import { getSocietyByName, getSocieties, getSocietyById } from '../../helpers/api/callSociety';
import { isNameValid, isEmailValid } from '../../helpers/functions/validation';

// Types
import { UserData } from '../../types/userData';
import { SocietyData } from '../../types/societyData';
import { useUserStore } from '../../store/UserStore';
import { useSessionStore } from '../../store/SessionStore';
import { InternData } from '../../types/internData';
import { ButtonStatus } from '../../types/enumButtonStatus';

interface DrawerCreateSessionProps {
    buttonStatus: ButtonStatus;
    scrollPosition?: 'top' | 'middle' | 'bottom';
    traineesId: string[];
    sessionId: string;
    updatingTrainee?: InternData;
}

type FormValues = UserData;

const DrawerAddIntern = forwardRef(({ buttonStatus, scrollPosition, traineesId, sessionId, updatingTrainee }: DrawerCreateSessionProps, ref) => {
    const theme = useTheme();
    const { user, setUser } = useUserStore();
    const { session } = useSessionStore();
    const [societies, setSocieties] = useState<SocietyData[] | null>(null);
    const [selectedSociety, setSelectedSociety] = useState<SocietyData | null>(null);
    const [sessionSocietyName, setSessionSocietyName] = useState<string>('');

    const isIntra = session.typeTraining === 'Intra';

    const fetchSocieties = async () => {
        const societies = await getSocieties();
        setSocieties(societies);
    };

    const getSocietyName = async () => {
        updatingTrainee?.societyId && setSelectedSociety(await getSocietyById(updatingTrainee.societyId));
    };

    const {
        register,
        handleSubmit,
        control,
        resetField,
        formState: { errors },
        clearErrors,
        setValue
    } = useForm<FormValues>();

    const { handleCreateOrAddTrainee, handleEditTrainee, handleDeleteTrainee, error, setError, isLoading, isDrawerOpen, openDrawer, closeDrawer } = useTrainee(
        sessionId,
        traineesId,
        updatingTrainee || ({} as InternData)
    );

    useImperativeHandle(ref, () => ({
        openDrawer: async () => {
            await getSocietyName();
            await fetchSocieties();
            await hydrateSessionSociety();
            openDrawer();
        },
        closeDrawer: () => closeDrawer()
    }));

    const handleOpenDrawer = async () => {
        await getSocietyName();
        await fetchSocieties();
        await hydrateSessionSociety();
        openDrawer();
    };

    const handleCloseDrawer = () => {
        resetField('civility');
        resetField('firstname');
        resetField('lastname');
        resetField('societyId');
        resetField('email');
        resetField('registrationNumber');
        setError(null);
        closeDrawer();
    };

    const civilityOptions = i18n.language === 'fr-FR' ? ['M.', 'Mme', 'Autre'] : ['Mr.', 'Mrs.', 'Other'];

    const hydrateSessionSociety = async () => {
        if (isIntra && session?.societyId) {
            const s = await getSocietyById(session.societyId);
            setSessionSocietyName(s?.name ?? '');
            setValue('societyId', String(session.societyId));
        }
    };

    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        if (!data.societyId) {
            let societyId = user.nullSocietyId;

            if (!societyId) {
                const society = await getSocietyByName('N/A');
                societyId = society?.id?.toString();
                setUser({ nullSocietyId: societyId });
            }

            data.societyId = societyId;
        }

        try {
            updatingTrainee ? await handleEditTrainee(data as InternData) : await handleCreateOrAddTrainee(data as InternData);
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <>
            {buttonStatus === ButtonStatus.FLOAT ? (
                <FloatingButton isExtended={scrollPosition === 'top'} onClick={handleOpenDrawer} label={i18n.t('AddIntern.title')} icon={<AddCircleOutlineIcon />} />
            ) : buttonStatus === ButtonStatus.FIXED ? (
                <Button variant="contained" sx={{ height: '3.5em', marginTop: '1em', color: 'white', backgroundColor: '#1A82AF', borderRadius: '0.85em' }} onClick={handleOpenDrawer}>
                    <AddCircleOutlineIcon />
                    <Typography variant="button" sx={{ marginLeft: '0.5em', fontSize: '1.1em', textTransform: 'none' }}>
                        {i18n.t('AddIntern.title')}
                    </Typography>
                </Button>
            ) : null}
            <Drawer
                anchor="bottom"
                PaperProps={{
                    sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        borderRadius: '15px 15px 0 0',
                        paddingBottom: '1em',
                        width: '100%',
                        height: 'auto',
                        backgroundColor: theme.palette.background.default,
                        backgroundImage: 'none'
                    }
                }}
                open={isDrawerOpen}
                onClose={handleCloseDrawer}
            >
                <IconButton onClick={handleCloseDrawer} sx={{ alignSelf: 'flex-end', margin: '0.5em' }}>
                    <CloseIcon sx={{ color: theme.palette.text.custom, fontSize: '1.05em' }} />
                </IconButton>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '87%', color: '#000', marginTop: '-1em' }}>
                    <TitleContentInfo title={updatingTrainee ? i18n.t('AddIntern.titleUpdate') : i18n.t('AddIntern.title')} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', margin: '0.5em auto 0 auto', width: '100%', height: '25em' }}>
                        <Controller
                            name="civility"
                            defaultValue={updatingTrainee?.civility || civilityOptions[0]}
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel
                                        id="demo-simple-select-label"
                                        sx={{
                                            fontSize: '0.85em',
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused': { color: theme.palette.text.custom }
                                        }}
                                    >
                                        {i18n.t('AddIntern.inputs.civility')}
                                    </InputLabel>
                                    <Select
                                        {...field}
                                        labelId="demo-simple-select-label"
                                        label={i18n.t('AddIntern.inputs.civility')}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        sx={{
                                            maxHeight: '3em',
                                            borderRadius: '10px',
                                            color: theme.palette.text.primary,
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.text.custom
                                            }
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    boxShadow: 'none',
                                                    backgroundColor: 'transparent'
                                                }
                                            },
                                            MenuListProps: {
                                                sx: {
                                                    width: '100%',
                                                    backgroundColor: theme.palette.background.paper,
                                                    color: theme.palette.text.primary,
                                                    borderRadius: '20px',
                                                    marginTop: '0.5em'
                                                }
                                            }
                                        }}
                                    >
                                        {civilityOptions.map((option) => (
                                            <MenuItem sx={{ margin: '0 auto', width: '95%', borderRadius: '10px' }} key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        <SignEvalTextField
                            icon={<AccountCircleOutlinedIcon />}
                            iconPosition="end"
                            defaultValue={updatingTrainee?.firstname}
                            registerProps={{
                                ...register('firstname', {
                                    required: true,
                                    validate: (value) => isNameValid(value || '') || i18n.t('AddIntern.errors.firstName')
                                }),
                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                    clearErrors('firstname');
                                    setError(null);
                                    setValue('firstname', e.target.value);
                                }
                            }}
                            id="sessionName"
                            label={i18n.t('AddIntern.inputs.firstName') + ' *'}
                            error={!!errors.firstname}
                            type="text"
                        />
                        <SignEvalTextField
                            icon={<AccountCircleOutlinedIcon />}
                            iconPosition="end"
                            defaultValue={updatingTrainee?.lastname}
                            registerProps={{
                                ...register('lastname', {
                                    required: true,
                                    validate: (value) => isNameValid(value || '') || i18n.t('AddIntern.errors.lastName')
                                }),
                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                    clearErrors('lastname');
                                    setError(null);
                                    setValue('lastname', e.target.value);
                                }
                            }}
                            id="sessionName"
                            label={i18n.t('AddIntern.inputs.lastName') + ' *'}
                            error={!!errors.lastname}
                            type="text"
                        />
                        <Controller
                            name="societyId"
                            control={control}
                            defaultValue={updatingTrainee?.societyId ?? (isIntra ? session?.societyId : undefined)}
                            render={({ field }) =>
                                isIntra ? (
                                    <>
                                        <SignEvalTextField
                                            label={i18n.t('AddIntern.inputs.society')}
                                            value={sessionSocietyName}
                                            disabled
                                            InputLabelProps={{ sx: { fontSize: '0.85em', color: theme.palette.text.secondary } }}
                                        />
                                        <input type="hidden" {...field} value={session?.societyId ?? ''} />
                                    </>
                                ) : (
                                    <Autocomplete
                                        value={selectedSociety?.name ?? null}
                                        options={societies?.map((s) => s.name) || []}
                                        noOptionsText={i18n.t('CreateSession.errors.noTrainings')}
                                        onChange={(e, name) => {
                                            const s = societies?.find((x) => x.name === name);
                                            setSelectedSociety(s ?? null);
                                            field.onChange(s?.id ?? null);
                                        }}
                                        renderInput={(params) => (
                                            <SignEvalTextField
                                                {...params}
                                                label={i18n.t('AddIntern.inputs.society')}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    sx: { maxHeight: '3em', borderRadius: '10px', color: theme.palette.text.primary }
                                                }}
                                                InputLabelProps={{ sx: { fontSize: '0.85em', color: theme.palette.text.primary } }}
                                            />
                                        )}
                                        ListboxProps={{
                                            sx: {
                                                width: '100%',
                                                backgroundColor: theme.palette.background.paper,
                                                color: theme.palette.text.primary,
                                                borderRadius: '10px',
                                                marginTop: '0.5em',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                maxHeight: '9em',
                                                overflow: 'auto',
                                                listStyle: 'none',
                                                position: 'absolute'
                                            }
                                        }}
                                    />
                                )
                            }
                        />
                        <SignEvalTextField
                            icon={<MailOutlinedIcon />}
                            iconPosition="end"
                            defaultValue={updatingTrainee?.email}
                            registerProps={{
                                ...register('email', {
                                    required: false,
                                    validate: (value) => !value || isEmailValid(value) || i18n.t('AddIntern.errors.email')
                                }),
                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                    clearErrors('email');
                                    setError(null);
                                    setValue('email', e.target.value);
                                }
                            }}
                            id="sessionName"
                            label={i18n.t('AddIntern.inputs.email')}
                            error={!!errors.email}
                            type="email"
                        />
                        <SignEvalTextField
                            icon={<AccountCircleOutlinedIcon />}
                            iconPosition="end"
                            defaultValue={updatingTrainee?.registrationNumber}
                            registerProps={{
                                ...register('registrationNumber', {
                                    required: false,
                                    pattern: /^[a-zA-Z0-9]{1,}$/
                                }),
                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                    clearErrors('registrationNumber');
                                    setError(null);
                                    setValue('registrationNumber', e.target.value);
                                }
                            }}
                            id="sessionName"
                            label={i18n.t('AddIntern.inputs.registrationNumber')}
                            error={!!errors.registrationNumber}
                            type="text"
                        />
                        <Typography sx={{ textAlign: 'center', fontSize: '0.7em', color: theme.palette.error.main }}>{error}</Typography>
                        <Typography sx={{ fontSize: '0.7em', marginRight: 'auto', color: theme.palette.text.primary }}>{i18n.t('Login.RequiredFields') + '*'}</Typography>
                    </Box>
                    {isLoading ? (
                        <CircularProgress sx={{ margin: '1.5em auto 1em auto', height: '3em', color: theme.palette.text.custom }} />
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'center', gap: '1em' }}>
                            {updatingTrainee && (
                                <SignEvalButton sx={{ margin: '1.5em 0 1em 0' }} size="medium" backgroundColor="red" onClick={() => handleDeleteTrainee(updatingTrainee.id)}>
                                    {i18n.t('AddIntern.button.delete')}
                                </SignEvalButton>
                            )}
                            <SignEvalButton sx={{ margin: '1.5em 0 1em 0' }} size={updatingTrainee ? 'medium' : 'large'} backgroundColor="blue" onClick={handleSubmit(onSubmit)}>
                                {updatingTrainee ? i18n.t('AddIntern.button.update') : i18n.t('AddIntern.button.add')}
                            </SignEvalButton>
                        </Box>
                    )}
                </Box>
            </Drawer>
        </>
    );
});

DrawerAddIntern.displayName = 'DrawerAddIntern';
export default DrawerAddIntern;
